import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import LoginForm from 'Components/AuthForm/LoginForm';
import { usePrimarySignIn } from 'Hooks/Auth';
import { useBeaconRemove, useReferralTracking } from 'Hooks/Common';
import useIsMobile from 'Hooks/Common/useIsMobile';
import { UnauthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { DataLayerAnalytics } from 'Services/Integrations';
import Toast from 'Services/Toast';
import { isNotEmpty } from 'Utils/functions';
import {
  isEmailConfirmationData,
  isTwoFactorResponseData,
  isUserResponseData,
} from 'Utils/typeGuards';

const Login = () => {
  useBeaconRemove();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [callSignIn, isLoading] = usePrimarySignIn();
  const referralTracking = useReferralTracking();

  const signIn = useCallback(
    async values => {
      try {
        const response = await callSignIn(values);

        if (!isNotEmpty(response)) {
          return;
        }

        if (isUserResponseData(response)) {
          DataLayerAnalytics.fireUserIdEvent(response.user);
        }

        if (isTwoFactorResponseData(response)) {
          navigate(UnauthorizedRoutePaths.TWO_FACTOR);
        }

        if (isEmailConfirmationData(response)) {
          navigate(UnauthorizedRoutePaths.CONFIRM_ACCOUNT, {
            state: { confirmationRequired: true },
          });
        }

        if (isUserResponseData(response) && response.isNewUser) {
          DataLayerAnalytics.fireGoogleRegistrationEvent();
          Toast.success('Your account has been created.');
        }

        if (isUserResponseData(response)) {
          await referralTracking(response.user.email as string, response.user.customerId);
        }
      } catch (error) {
        Toast.error(error.message);
      }
    },
    [callSignIn, navigate, referralTracking],
  );

  return (
    <div className="auth">
      <Helmet>
        <meta name="description" content="Log in to Signaturely." />
        <title>Log In | Signaturely</title>
      </Helmet>
      <h1 className={classNames('auth__title', { mobile: isMobile })}>
        Login to your account
      </h1>
      <LoginForm
        isLoading={isLoading}
        onSubmit={signIn}
        formClassName={isMobile ? 'mobile' : ''}
      />
    </div>
  );
};

export default Login;
